import * as THREE from 'three'
import { texturaDaVisibilidade } from './edgeVisibility'
import type { FragmentsModel } from '@thatopen/fragments'

// ============================================================================
// paintEngine: Fragments-bypass colour overlay (2026-05-10)
//
// Background:
//   @thatopen/fragments@3.4 colours items via model.setColor(ids, hex), which
//   internally allocates HighlightHelper bitmask slots: one per (item, hex)
//   tuple. The bitmask never reliably frees: even resetColor(undefined)
//   itself trips "Memory overflow!" inside ItemConfigController once the
//   bitmask is saturated. There is no recovery without a page refresh.
//   See docs/upstream-fragments-leak.md for the upstream report draft.
//
// Approach:
//   * We do NOT call model.setColor / model.resetColor. The bitmask is left
//     untouched.
//   * Each tile mesh's geometry already carries a per-vertex `id` attribute
//     (Float32 localId). We don't read it on the CPU; that array is
//     deleted after GPU upload. Instead, we let the shader sample it.
//   * For each model we keep an RGBA DataTexture (1024×1024) that maps
//     localId → colour. Slot index = y * 1024 + x. alpha=0 means
//     "passthrough" (no override; original Fragments material colour wins).
//   * Each MeshLambertMaterial we encounter on a tile is patched once via
//     onBeforeCompile to inject:
//       - vertex shader: read `attribute float id`, pass via varying.
//       - fragment shader: lookup the paint texture and overwrite
//         diffuseColor.rgb when alpha > 0.5.
//   * Painting an item is a CPU write to the texture pixel buffer + texture
//     needsUpdate. Zero worker round-trips, zero bitmask growth.
//
// Memory: 1024 × 1024 × 4 bytes = 4 MB per model. Acceptable.
// Capacity: 1,048,576 distinct localIds per model. Plenty for any IFC.
// ============================================================================

const TEX_SIDE = 1024
const TEX_SLOTS = TEX_SIDE * TEX_SIDE

interface ModelPaintState {
  modelId: string
  pixels: Uint8Array
  texture: THREE.DataTexture
  /** Materials we've already patched (avoid double-injection on reuse). */
  patchedMaterials: WeakSet<THREE.Material>
  /** Strong refs to patched materials so debug helpers can iterate them
   *  to force recompiles (WeakSet doesn't iterate). */
  patchedList: Set<THREE.Material>
}

const states = new Map<string, ModelPaintState>()

function ensureState(modelId: string): ModelPaintState {
  let s = states.get(modelId)
  if (s) return s
  const pixels = new Uint8Array(TEX_SLOTS * 4) // all zero: alpha=0 = passthrough
  const texture = new THREE.DataTexture(
    pixels,
    TEX_SIDE,
    TEX_SIDE,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
  )
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.needsUpdate = true
  s = {
    modelId,
    pixels,
    texture,
    patchedMaterials: new WeakSet(),
    patchedList: new Set(),
  }
  states.set(modelId, s)
  return s
}

/** Parse any CSS colour string THREE.Color understands ('#rgb', '#rrggbb',
 *  'hsl(h, s%, l%)', named colours like 'red', etc.) into an [R, G, B]
 *  byte triplet. Falls back to white on invalid input rather than black,
 *  so a typo in a caller's hex doesn't disappear into the dark. */
const _scratchColor = new THREE.Color()
function parseColorToRgb(str: string): [number, number, number] {
  try {
    _scratchColor.set(str)
    return [
      Math.round(_scratchColor.r * 255),
      Math.round(_scratchColor.g * 255),
      Math.round(_scratchColor.b * 255),
    ]
  } catch {
    console.warn('[paintEngine] could not parse colour', str)
    return [255, 255, 255]
  }
}

/** Write a single (localId → hex) entry into the model's paint texture.
 *  alpha=255 means "override the diffuse colour with this rgb". */
export function setItemColor(
  modelId: string,
  localId: number,
  hex: string,
): void {
  if (localId < 0 || localId >= TEX_SLOTS) return
  const s = ensureState(modelId)
  const [r, g, b] = parseColorToRgb(hex)
  const idx = localId * 4
  s.pixels[idx] = r
  s.pixels[idx + 1] = g
  s.pixels[idx + 2] = b
  s.pixels[idx + 3] = 255
  s.texture.needsUpdate = true
}

/** Bulk write. Returns the number of writes that fit. */
export function setItemColors(
  modelId: string,
  pairs: Iterable<[number, string]>,
): number {
  const s = ensureState(modelId)
  let n = 0
  for (const [localId, hex] of pairs) {
    if (localId < 0 || localId >= TEX_SLOTS) continue
    const [r, g, b] = parseColorToRgb(hex)
    const idx = localId * 4
    s.pixels[idx] = r
    s.pixels[idx + 1] = g
    s.pixels[idx + 2] = b
    s.pixels[idx + 3] = 255
    n += 1
  }
  s.texture.needsUpdate = true
  return n
}

/** Clear a single entry: go back to the original Fragments material colour. */
export function clearItemColor(modelId: string, localId: number): void {
  const s = states.get(modelId)
  if (!s) return
  if (localId < 0 || localId >= TEX_SLOTS) return
  const idx = localId * 4
  s.pixels[idx + 3] = 0 // alpha = 0 = passthrough
  s.texture.needsUpdate = true
}

/** Wipe every override for the model. */
export function clearAllItemColors(modelId: string): void {
  const s = states.get(modelId)
  if (!s) return
  s.pixels.fill(0)
  s.texture.needsUpdate = true
}

/** Drop everything for an unloaded model. */
export function disposeModelPaint(modelId: string): void {
  const s = states.get(modelId)
  if (!s) return
  s.texture.dispose()
  states.delete(modelId)
}

/** Patch a MeshLambertMaterial so the fragment shader looks up our paint
 *  texture by per-vertex `id` and overrides diffuseColor when alpha > 0.5.
 *  Idempotent: calling on an already-patched material is a no-op. */
/** Debug switch: every patched material renders bright magenta. Off in
 *  production; toggle from DevTools via `window.__vn2rPaintTest.forceMagenta(true)`. */
let FORCE_MAGENTA_DEBUG = false

/** Debug switch: every item rendered with a hue derived from its `id`
 *  attribute (no texture lookup). Useful to confirm `id` reaches the
 *  shader. Off in production. */
const DEBUG_ID_AS_COLOR = false

/** Stats counter so we can see in the console which patches actually ran. */
const patchStats = {
  attempted: 0,
  patched: 0,
  compiled: 0,
}

function patchMaterial(
  material: THREE.Material,
  state: ModelPaintState,
): void {
  patchStats.attempted += 1
  if (state.patchedMaterials.has(material)) return
  if (!(material as THREE.MeshLambertMaterial).isMaterial) return
  // Only patch MeshLambertMaterial: that's what Fragments uses for SHELL
  // (solid BIM geometry). LodMaterial / LineBasicMaterial / others use
  // different shader chunks and our injection breaks them.
  // We check `material.type` (string field set by THREE.js, never
  // minified) instead of `constructor.name` (which Rolldown does
  // minify in production builds, breaking the filter, bug seen in
  // 0.2.0-beta.1's first deploy).
  const matType =
    (material as THREE.Material & { type?: string }).type ?? ''
  const matName = material.constructor?.name ?? ''
  const isLambert =
    matType === 'MeshLambertMaterial' ||
    matName === 'MeshLambertMaterial' ||
    !!(material as THREE.MeshLambertMaterial & {
      isMeshLambertMaterial?: boolean
    }).isMeshLambertMaterial
  if (!isLambert) return
  // NÃO patchear os materiais de HIGHLIGHT do Fragments. Quando um item
  // é seleccionado/hover, o Fragments divide a geometria em grupos e
  // atribui um MeshLambertMaterial próprio do estilo, identificado por
  // material.userData.customId ('select', 'hover', 'ghost', ...). Se o
  // patch de paint se aplicar a esse material, a tinta do Colorir por…/
  // Cores por classe sobrepõe-se ao tom de seleção e o pré-selecionar/
  // selecionar deixa de se ver em elementos pintados. Exceção: 'fade'
  // preserva a cor original de propósito, por isso deve manter o paint.
  const customId = (
    material.userData as { customId?: unknown } | undefined
  )?.customId
  if (typeof customId === 'string' && customId !== 'fade') return
  state.patchedMaterials.add(material)
  state.patchedList.add(material)
  patchStats.patched += 1

  const prevOnBeforeCompile = material.onBeforeCompile
  material.onBeforeCompile = (shader, renderer) => {
    if (prevOnBeforeCompile) prevOnBeforeCompile(shader, renderer)
    shader.uniforms.vn2rPaintTex = { value: state.texture }
    shader.uniforms.vn2rFaceVisTex = { value: texturaDaVisibilidade(state.modelId) }
    shader.uniforms.vn2rPaintSide = { value: TEX_SIDE }
    shader.uniforms.vn2rForceMagenta = { value: FORCE_MAGENTA_DEBUG ? 1 : 0 }
    shader.uniforms.vn2rDebugIdColor = { value: DEBUG_ID_AS_COLOR ? 1 : 0 }

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float id;
flat varying float vVn2rItemId;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vVn2rItemId = id;`,
      )

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform sampler2D vn2rPaintTex;
uniform sampler2D vn2rFaceVisTex;
uniform float vn2rPaintSide;
uniform int vn2rForceMagenta;
uniform int vn2rDebugIdColor;
flat varying float vVn2rItemId;
vec4 vn2rGetPaint() {
  float idx = vVn2rItemId;
  float xi = mod(idx, vn2rPaintSide);
  float yi = floor(idx / vn2rPaintSide);
  vec2 uv = (vec2(xi, yi) + 0.5) / vn2rPaintSide;
  return texture2D(vn2rPaintTex, uv);
}
// Fantasma por contornos (viewer/lib/ghostContornos.ts): o elemento fica
// só com as arestas, por isso a face não se desenha. 128 na textura da
// visibilidade das arestas quer dizer "em fantasma".
bool vn2rEmFantasma() {
  float idx = vVn2rItemId;
  float xi = mod(idx, vn2rPaintSide);
  float yi = floor(idx / vn2rPaintSide);
  vec2 uv = (vec2(xi, yi) + 0.5) / vn2rPaintSide;
  float v = texture2D(vn2rFaceVisTex, uv).r;
  return v > 0.25 && v < 0.75;
}
vec3 vn2rIdAsColor() {
  // Hash the id into a hue using the golden ratio for nice spread.
  float h = fract(vVn2rItemId * 0.61803398875);
  // Simple HSV->RGB with full saturation and 0.6 value.
  vec3 k = vec3(5.0, 3.0, 1.0);
  vec3 p = abs(fract(vec3(h) + k / 6.0) * 6.0 - 3.0);
  return clamp(p - 1.0, 0.0, 1.0);
}`,
      )
      .replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
if (vn2rEmFantasma()) discard;`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
if (vn2rForceMagenta == 1) {
  diffuseColor.rgb = vec3(1.0, 0.0, 1.0);
} else if (vn2rDebugIdColor == 1) {
  diffuseColor.rgb = vn2rIdAsColor();
} else {
  vec4 vn2rPaint = vn2rGetPaint();
  if (vn2rPaint.a > 0.5) {
    diffuseColor.rgb = vn2rPaint.rgb;
  }
}`,
      )

    patchStats.compiled += 1
    void matName // suppress unused-variable warning; the var is kept
    // because the diagnostic inspectModel() reads it.
  }
  // Force three.js to use a new cache key so the shader is re-linked even
  // for materials that were already rendered (and thus already cached)
  // before we attached the onBeforeCompile hook.
  ;(material as THREE.Material & {
    customProgramCacheKey?: () => string
  }).customProgramCacheKey = () => `vn2rPaint2:${state.modelId}`
  material.needsUpdate = true
}

/** Inspect the first SHELL mesh of the model and log its geometry attribute
 *  names + types. Helps figure out whether `id` is float / uint / etc. */
export function inspectModel(model: FragmentsModel): void {
  let logged = 0
  model.object.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh || logged >= 3) return
    const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
    const matName = mat?.constructor?.name ?? '?'
    const attrs: Record<string, string> = {}
    for (const [name, attr] of Object.entries(mesh.geometry.attributes)) {
      const a = attr as THREE.BufferAttribute
      const arrCtor = a.array?.constructor?.name ?? '(deleted)'
      attrs[name] = `${arrCtor} itemSize=${a.itemSize} count=${a.count}`
    }
    console.log(
      `[paintEngine] mesh #${logged}: material=${matName} attrs=`,
      attrs,
      'groups=',
      mesh.geometry.groups,
    )
    logged += 1
  })
  console.log('[paintEngine] patch stats:', { ...patchStats })
}

/** Walk the model's scene graph, find every Mesh with a SHELL-style
 *  MeshLambertMaterial, and apply the paint patch + hook into the model's
 *  onViewUpdated stream so newly-spawned tiles also get the patch.
 *
 *  Idempotent: safe to call repeatedly. We only register the
 *  onViewUpdated listener once per model. */
const installedHooks = new Set<string>()

export function attachPaintToModel(model: FragmentsModel): void {
  const state = ensureState(model.modelId)
  const patchAllTiles = () => {
    model.object.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      const mat = mesh.material
      if (Array.isArray(mat)) {
        for (const m of mat) patchMaterial(m, state)
      } else if (mat) {
        patchMaterial(mat, state)
      }
    })
  }
  patchAllTiles()
  if (!installedHooks.has(model.modelId)) {
    installedHooks.add(model.modelId)
    model.onViewUpdated.add(() => {
      // New tiles arrive with their own materials; patch them too.
      patchAllTiles()
    })
  }
}

/** Dispose the per-model state. Called from viewerApi.disposeModel. */
export function detachPaintFromModel(modelId: string): void {
  installedHooks.delete(modelId)
  disposeModelPaint(modelId)
}

/** Browser console helpers for the POC phase. Open DevTools and call:
 *
 *    __vn2rPaintTest.paintAll(modelId, '#ff00ff')   // tint every item magenta
 *    __vn2rPaintTest.paintRange(modelId, 0, 1000, '#22d3ee')
 *    __vn2rPaintTest.clear(modelId)
 *
 *  Use the modelId shown in the sidebar (filename usually).
 */
declare global {
  interface Window {
    __vn2rPaintTest?: {
      models: () => string[]
      stats: () => typeof patchStats
      forceMagenta: (on: boolean) => void
      paintEverything: (hex: string) => void
      clearEverything: () => void
      setItemColor: typeof setItemColor
      setItemColors: typeof setItemColors
      clearItemColor: typeof clearItemColor
      clearAll: (modelId: string) => void
      paintAll: (modelId: string, hex: string) => void
      paintRange: (
        modelId: string,
        from: number,
        to: number,
        hex: string,
      ) => number
    }
  }
}

if (typeof window !== 'undefined') {
  window.__vn2rPaintTest = {
    models: () => Array.from(states.keys()),
    stats: () => ({ ...patchStats }),
    forceMagenta: (on) => {
      FORCE_MAGENTA_DEBUG = on
      // Force every patched material to recompile by bumping its cache key
      // and setting needsUpdate. The new uniform initial value flows in
      // when onBeforeCompile fires again on the next render.
      let bumped = 0
      const cacheBumpCounter = Date.now()
      for (const s of states.values()) {
        for (const mat of s.patchedList) {
          ;(mat as THREE.Material & {
            customProgramCacheKey?: () => string
          }).customProgramCacheKey = () =>
            `vn2rPaint:${s.modelId}:m${cacheBumpCounter}`
          mat.needsUpdate = true
          bumped += 1
        }
      }
      console.log(
        `[paintEngine] forceMagenta=${on}; bumped ${bumped} materials. Move the camera or wait for a tile update to trigger re-render.`,
      )
    },
    paintEverything: (hex) => {
      for (const modelId of states.keys()) {
        const pairs: [number, string][] = []
        for (let i = 0; i < TEX_SLOTS; i++) pairs.push([i, hex])
        setItemColors(modelId, pairs)
      }
    },
    clearEverything: () => {
      for (const modelId of states.keys()) clearAllItemColors(modelId)
    },
    setItemColor,
    setItemColors,
    clearItemColor,
    clearAll: clearAllItemColors,
    paintAll: (modelId, hex) => {
      const pairs: [number, string][] = []
      for (let i = 0; i < TEX_SLOTS; i++) pairs.push([i, hex])
      setItemColors(modelId, pairs)
    },
    paintRange: (modelId, from, to, hex) => {
      const pairs: [number, string][] = []
      for (let i = from; i < to; i++) pairs.push([i, hex])
      return setItemColors(modelId, pairs)
    },
  }
}
